import React from 'react';

import { SeatingTabClass } from 'Services/Api/_HumanWritten/SeatingService';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { describeClassPoints } from './seatingLayout';

interface Props {
	passengerClass: SeatingTabClass;
	onClose: () => void;
}

/**
 * Everything a class offers, opened from the ⓘ beside its name.
 *
 * <p>The list beside a class in the picker is deliberately short — it is what one class is compared
 * against another by, and eight lines against two stops being a comparison. This is where the rest
 * lives, for a booker who has narrowed it down and wants the detail.</p>
 *
 * <p>Inline rather than a portal-mounted modal: it opens inside the class row it belongs to, so it
 * needs no positioning logic and cannot end up anchored to the wrong row on a scrolling step.</p>
 */
export function ClassMoreInfo({ passengerClass, onClose }: Props) {
	const points = (passengerClass.attributes ?? []).filter(x => x.showInMoreInfo);
	const description = describeClassPoints(passengerClass.description);

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-3 flex flex-col gap-2">
			<div className="flex items-start justify-between gap-3">
				<span className="text-sm font-semibold text-gray-900">{passengerClass.name}</span>
				<button
					type="button"
					className="text-xs text-gray-500 underline"
					onClick={onClose}
				>
					Close
				</button>
			</div>

			{description.length > 0 && (
				<div className="text-xs text-gray-600">
					{description.length === 1 ? (
						<p>{description[0]}</p>
					) : (
						<ul className="list-disc pl-4 flex flex-col gap-0.5">
							{description.map(point => (
								<li key={point}>{point}</li>
							))}
						</ul>
					)}
				</div>
			)}

			{points.length > 0 && (
				<ul className="flex flex-col gap-1.5">
					{points.map(point => (
						<li key={point.label} className="flex items-center gap-2 text-xs text-gray-800">
							{point.iconName ? (
								<Icon name={point.iconName} useMaterialIcons />
							) : (
								// Keeps the labels aligned when only some points carry an icon.
								<span className="w-4" aria-hidden="true" />
							)}
							<span>{point.label}</span>
						</li>
					))}
				</ul>
			)}

			{points.length === 0 && description.length === 0 && (
				<p className="text-xs text-gray-500">Nothing more to add about this fare type.</p>
			)}
		</div>
	);
}
